import { useAuthStore } from "@persisted-state/auth/auth.store";

export const GATEPASS_STATUSES = {
  AWAITING_FOTT_APPROVAL: "AWAITING_FOTT_APPROVAL",
  AWAITING_SPONSOR_COMPANY_APPROVAL: "AWAITING_SPONSOR_COMPANY_APPROVAL",
  AWAITING_SECURITY_DEPT_APPROVAL: "AWAITING_SECURITY_DEPT_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED",
} as const;

export const GATEPASS_TASK_ROLES = {
  FOTT: "FOTT",
  SPONSOR_COMPANY: "SPONSOR_COMPANY",
  // Security Department (Security Operator and Security Supervisor)
  SECURITY_DEPARTMENT: "SECURITY_DEPARTMENT",
  SECURITY_OPERATOR: "SECURITY_OPERATOR",
  SECURITY_SUPERVISOR: "SECURITY_SUPERVISOR",
} as const;

// Role values for Security Dept users who CAN claim/approve/reject (excludes Supervisor)
const SECURITY_OPERATOR_ROLES = [
  GATEPASS_TASK_ROLES.SECURITY_DEPARTMENT,
  GATEPASS_TASK_ROLES.SECURITY_OPERATOR,
  // Backend may return SECURITY_DEPT as generic security department task
  "SECURITY_DEPT",
] as const;

// Role values that indicate the task is for Security Department
const SECURITY_DEPT_ROLES = [
  ...SECURITY_OPERATOR_ROLES,
  GATEPASS_TASK_ROLES.SECURITY_SUPERVISOR,
] as const;

// Normalize role for comparison (handles backend returning different casing/hyphens)
const normalizeRole = (role?: string) => (role ?? "").toUpperCase().replace(/-/g, "_");

const roleMatches = (role: string | undefined, allowed: readonly string[]) =>
  normalizeRole(role) &&
  (allowed as readonly string[]).some((r) => normalizeRole(r) === normalizeRole(role));

export const TASK_STATUSES = {
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  TERMINATED: "TERMINATED",
} as const;

// Statuses where a task can be claimed
export const CLAIMABLE_GATEPASS_STATUSES = [
  GATEPASS_STATUSES.AWAITING_FOTT_APPROVAL,
  GATEPASS_STATUSES.AWAITING_SPONSOR_COMPANY_APPROVAL,
  GATEPASS_STATUSES.AWAITING_SECURITY_DEPT_APPROVAL,
];

const APPROVABLE_REJECTABLE_STATUSES = [
  GATEPASS_STATUSES.AWAITING_FOTT_APPROVAL,
  GATEPASS_STATUSES.AWAITING_SPONSOR_COMPANY_APPROVAL,
  GATEPASS_STATUSES.AWAITING_SECURITY_DEPT_APPROVAL,
];

const isTaskNotClaimed = (taskStatus: string) => taskStatus === TASK_STATUSES.READY;
const isTaskClaimed = (taskStatus: string) => taskStatus === TASK_STATUSES.CLAIMED;
const isTaskOwner = (claimOwnerName: string | null | undefined) =>
  claimOwnerName === useAuthStore.getState().user?.preferredUsername;
const isUserClaimedTaskOwner = (taskStatus: string, claimOwnerName: string | null | undefined) =>
  (isTaskClaimed(taskStatus) || Boolean(claimOwnerName)) && isTaskOwner(claimOwnerName);
const isGatepassClaimable = (status: string) =>
  (CLAIMABLE_GATEPASS_STATUSES as readonly string[]).includes(status);
const isGatepassApprovableOrRejectable = (status: string) =>
  (APPROVABLE_REJECTABLE_STATUSES as readonly string[]).includes(status);

export const canUserClaim = (
  taskStatus: string,
  status: string,
  taskRole?: string,
  claimOwnerName?: string | null,
) =>
  isTaskNotClaimed(taskStatus) &&
  !claimOwnerName &&
  isGatepassClaimable(status) &&
  isTaskForFlowRole(status, taskRole);

/** Task is for FOTT when status is AWAITING_FOTT_APPROVAL and role is FOTT */
export const isTaskForFott = (status: string, taskRole?: string) =>
  status === GATEPASS_STATUSES.AWAITING_FOTT_APPROVAL && taskRole === GATEPASS_TASK_ROLES.FOTT;

/** Task is for Sponsor Company when status is AWAITING_SPONSOR_COMPANY_APPROVAL and role is SPONSOR_COMPANY */
export const isTaskForSponsorCompany = (status: string, taskRole?: string) =>
  status === GATEPASS_STATUSES.AWAITING_SPONSOR_COMPANY_APPROVAL &&
  taskRole === GATEPASS_TASK_ROLES.SPONSOR_COMPANY;

/** Task is for Security Department when status is AWAITING_SECURITY_DEPT_APPROVAL and role is Security Dept */
export const isTaskForSecurityDepartment = (status: string, taskRole?: string) =>
  status === GATEPASS_STATUSES.AWAITING_SECURITY_DEPT_APPROVAL &&
  taskRole != null &&
  roleMatches(taskRole, SECURITY_DEPT_ROLES);

// Whether the task is for a flow role that can claim/approve/reject.
const isTaskForFlowRole = (status: string, taskRole?: string) =>
  isTaskForFott(status, taskRole) ||
  isTaskForSponsorCompany(status, taskRole) ||
  isTaskForSecurityDepartment(status, taskRole);

const canUserActOnGatepass = (
  taskStatus: string,
  claimOwnerName: string | null | undefined,
  status: string,
  taskRole?: string,
) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
  isGatepassApprovableOrRejectable(status) &&
  isTaskForFlowRole(status, taskRole);

export const canUserUnclaim = (
  taskStatus: string,
  claimOwnerName: string | null | undefined,
  status: string,
  taskRole?: string,
) => canUserActOnGatepass(taskStatus, claimOwnerName, status, taskRole);

export const canUserApprove = (
  taskStatus: string,
  claimOwnerName: string | null | undefined,
  status: string,
  taskRole?: string,
) => canUserActOnGatepass(taskStatus, claimOwnerName, status, taskRole);

export const canUserReject = (
  taskStatus: string,
  claimOwnerName: string | null | undefined,
  status: string,
  taskRole?: string,
) => canUserActOnGatepass(taskStatus, claimOwnerName, status, taskRole);

// Whether to show the flow buttons (Claim/Unclaim/Approve/Reject) based on status and task role
export const canShowFlowButtons = (status: string, taskRole?: string) =>
  isTaskForFlowRole(status, taskRole);

// When an SC creates a request for another SC, only the gatepass's sponsor company can Claim/Approve/Reject
export const canShowFlowButtonsForUser = (
  status: string,
  taskRole?: string,
  userCompanyId?: string | null,
  gatepassSponsorCompany?: string | null,
): boolean => {
  if (!isTaskForFlowRole(status, taskRole)) return false;
  if (isTaskForSponsorCompany(status, taskRole)) {
    return (
      gatepassSponsorCompany != null &&
      gatepassSponsorCompany !== "" &&
      userCompanyId != null &&
      userCompanyId !== "" &&
      String(userCompanyId).trim() === String(gatepassSponsorCompany).trim()
    );
  }
  return true;
};

// Next gatepass status after the given role approves. Used for optimistic UI.
export const getNextStatusAfterApproval = (
  currentStatus: string,
  taskRole?: string,
  options?: {
    skipSponsorCompanyApproval?: boolean;
  },
): string => {
  if (isTaskForFott(currentStatus, taskRole)) {
    if (options?.skipSponsorCompanyApproval) {
      return GATEPASS_STATUSES.AWAITING_SECURITY_DEPT_APPROVAL;
    }
    return GATEPASS_STATUSES.AWAITING_SPONSOR_COMPANY_APPROVAL;
  }
  if (isTaskForSponsorCompany(currentStatus, taskRole)) {
    return GATEPASS_STATUSES.AWAITING_SECURITY_DEPT_APPROVAL;
  }
  if (isTaskForSecurityDepartment(currentStatus, taskRole)) {
    return GATEPASS_STATUSES.APPROVED;
  }
  return currentStatus;
};

// Pick the task the current user can act on from tasks array
export const pickTaskForFlowRole = <T extends { role?: string }>(
  status: string,
  task: T | null | undefined,
  tasks: T[] | null | undefined,
): T | null | undefined => {
  const taskList = task ? [task] : null;
  const list = tasks && tasks.length > 0 ? tasks : taskList;
  if (!list || list.length === 0) return task ?? null;
  if (list.length === 1) return list[0];
  const flowTask = list.find((t) => isTaskForFlowRole(status, t.role));
  return flowTask ?? list[0];
};
